import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IFarm } from 'interfaces/IFarm';
import type { IInput } from 'interfaces/IInput';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import type { FarmDiagnosticsTimeRange } from '../../../../redux/slices/InsightsSlice';
import { indexInputsByPaddock } from 'utils/FarmUtils';
import { resolveFarmYearInputYears } from './computeFarmDiagnosticsMetrics';
import {
  computeRotationHighlights,
  computeRotationRowWithTrend,
  resolveRotationScope,
} from './computeRotationEfficiency';
import { computeWueTrend } from './computeWueTrend';

/**
 * Rotation Efficiency's figures for every paddock in the resolved Farm/Org
 * scope, over the shared `rotationTimeRange` window and the farm-level
 * rainfall/practice in Redux. Shared by the workspace lens and the
 * read-only plan preview (OP-2490), so both show identical figures.
 */
// Stable identity for a paddock with no plans.
const NO_INPUTS: IInput[] = [];

export const useRotationEfficiencyData = (
  inputs: IInput[],
  farms: IFarm[],
  selectedFarmId: string | null,
  orgName: string,
  gUnits: GUnit[],
  /**
   * Replaces the workspace's Rotation efficiency range — the plan preview
   * passes its own Rotation years window so the averages cover exactly the
   * seasons its Rotation summary shows (OP-2490).
   */
  timeRangeOverride?: FarmDiagnosticsTimeRange
) => {
  const workspaceTimeRange = useSelector(
    (state: RootState) => state.insightsReducer.rotationTimeRange
  );
  const rotationTimeRange = timeRangeOverride ?? workspaceTimeRange;
  const farmYearInputsState = useSelector(
    (state: RootState) => state.insightsReducer.farmYearInputs
  );

  const scope = useMemo(
    () => resolveRotationScope(farms, selectedFarmId, orgName, gUnits),
    [farms, selectedFarmId, orgName, gUnits]
  );

  // Epic 1 is a pure in-memory function over the already-fetched `inputs`
  // array — calling it once per paddock in scope is N JS calls, not N HTTP
  // requests. Full (unfiltered) history is needed too, to derive the shared
  // window below (min/max years and "Last N years" resolution look across
  // each paddock's whole history, not just whatever's currently selected).
  // Indexed once, so each paddock's history is a lookup rather than a scan
  // of every plan in the organisation (OP-2490).
  const inputsByPaddock = useMemo(() => indexInputsByPaddock(inputs), [inputs]);

  const paddockAllRows = useMemo(
    () =>
      scope.paddocks.map((paddock) => ({
        paddock,
        allRows: computeWueTrend(
          inputsByPaddock.get(paddock.paddockId) ?? NO_INPUTS,
          paddock.paddockId,
          gUnits
        ),
      })),
    [scope.paddocks, inputsByPaddock, gUnits]
  );

  const { minYear, maxYear } = useMemo(() => {
    let min: number | undefined;
    let max: number | undefined;
    for (const { allRows } of paddockAllRows) {
      for (const row of allRows) {
        if (min === undefined || row.year < min) min = row.year;
        if (max === undefined || row.year > max) max = row.year;
      }
    }
    const currentYear = new Date().getFullYear();
    return { minYear: min ?? currentYear, maxYear: max ?? currentYear };
  }, [paddockAllRows]);

  // ONE shared calendar window for the WHOLE comparison, not each paddock
  // independently resolving its own "last N years". Paddocks are being
  // compared side by side, so they must be evaluated over the SAME years —
  // otherwise "Last 5 years" could mean 2022-2026 for one paddock and
  // 2015-2018 for another (whichever 5 years that paddock's own history
  // happens to end on), and a paddock could end up needing rainfall for
  // years no Farm-Level Inputs panel even offers (since those panels also
  // use this same shared window — see below). Unions every paddock's full
  // history first, then applies the current rotationTimeRange window once
  // across that combined set (reusing the same helper the Farm-Level Inputs
  // panels use, since it's exactly the same "one shared window" computation).
  const sharedWindowYears = useMemo(
    () => resolveFarmYearInputYears(paddockAllRows.map((p) => p.allRows), rotationTimeRange),
    [paddockAllRows, rotationTimeRange]
  );

  const paddockData = useMemo(() => {
    const windowSet = new Set(sharedWindowYears);
    return paddockAllRows.map(({ paddock, allRows }) => ({
      paddock,
      rangeRows: allRows.filter((row) => windowSet.has(row.year)),
    }));
  }, [paddockAllRows, sharedWindowYears]);

  // Trend now compares the earlier vs later half of whatever's in
  // `rangeRows` — entirely within the shared window above, so it never
  // needs data outside the years the Farm-Level Inputs panels already show.
  const rows = useMemo(
    () =>
      paddockData.map(({ paddock, rangeRows }) =>
        computeRotationRowWithTrend(
          paddock.paddockId,
          paddock.paddockName,
          paddock.areaHa,
          rangeRows,
          farmYearInputsState[paddock.farmId],
          gUnits
        )
      ),
    [paddockData, farmYearInputsState, gUnits]
  );

  const highlights = useMemo(() => computeRotationHighlights(rows), [rows]);

  // Every farm actually present in the current scope — Org scope can span
  // several, each with its own independently-entered rainfall/practice data
  // (farm-level, never a fabricated shared "org-level" value), but all shown
  // for the same sharedWindowYears so what a paddock's row needs and what
  // its farm's panel offers always match.
  const farmsInScope = useMemo(() => {
    const seen = new Map<string, string>();
    for (const paddock of scope.paddocks) {
      if (!seen.has(paddock.farmId)) {
        const farm = farms.find((f) => f.id === paddock.farmId);
        seen.set(paddock.farmId, farm?.farmName ?? '');
      }
    }
    return Array.from(seen.entries()).map(([farmId, farmName]) => ({ farmId, farmName }));
  }, [scope.paddocks, farms]);

  return {
    scope,
    inputsByPaddock,
    minYear,
    maxYear,
    sharedWindowYears,
    rows,
    highlights,
    farmsInScope,
  };
};
